import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Users,
  Search,
  Calendar,
  CreditCard,
  AlertCircle,
  CheckCircle,
  Clock,
  Trash2,
  Edit,
  Loader2,
  Building2,
  ChevronRight,
  HardDrive,
  UserCheck
} from 'lucide-react';

const API = RUNTIME_BACKEND_URL;

const subscriptionStatuses = {
  active: { label: 'Aktif', color: 'text-green-600 bg-green-100 dark:bg-green-900/30' },
  expired: { label: 'Süresi Dolmuş', color: 'text-red-600 bg-red-100 dark:bg-red-900/30' },
  pending: { label: 'Beklemede', color: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30' }
};

const subscriptionPlans = {
  monthly: 'Aylık Paket',
  yearly: 'Yıllık Paket'
};

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('all'); // all, photographer, salon_owner
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, pending, expired
  
  // Subscription dialog
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [subscriptionForm, setSubscriptionForm] = useState({
    subscription_plan: '',
    subscription_status: 'active',
    subscription_expires_at: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await fetch(`${API}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubscription = (user) => {
    setSelectedUser(user);
    setSubscriptionForm({
      subscription_plan: user.subscription_plan || 'monthly',
      subscription_status: user.subscription_status || 'active',
      subscription_expires_at: user.subscription_expires_at?.split('T')[0] || ''
    });
    setIsDialogOpen(true);
  };

  const handleSaveSubscription = async () => {
    if (!selectedUser) return;
    
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API}/api/admin/users/${selectedUser.id}/subscription`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subscription_plan: subscriptionForm.subscription_plan,
          subscription_status: subscriptionForm.subscription_status,
          subscription_expires_at: subscriptionForm.subscription_expires_at 
            ? new Date(subscriptionForm.subscription_expires_at).toISOString()
            : null
        })
      });
      
      if (!res.ok) throw new Error('Güncellenemedi');
      
      toast.success('Abonelik güncellendi');
      setIsDialogOpen(false);
      loadUsers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`"${user.full_name}" kullanıcısını ve tüm verilerini silmek istediğinize emin misiniz?`)) {
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/admin/users/${user.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Silinemedi');
      
      toast.success('Kullanıcı silindi');
      loadUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleApproveUser = async (user) => {
    try {
      // Abonelik durumunu active yap, 30 günlük trial ver
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30);
      
      const res = await fetch(`${API}/api/admin/users/${user.id}/subscription`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subscription_plan: 'monthly',
          subscription_status: 'active',
          subscription_expires_at: expiresAt.toISOString()
        })
      });
      
      if (!res.ok) throw new Error('Onaylanamadı');
      
      toast.success(`${user.full_name} onaylandı!`);
      loadUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const filteredUsers = users.filter(u => {
    // Arama filtresi
    const matchSearch = !searchTerm ||
      u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Kullanıcı tipi filtresi
    const matchUserType = userTypeFilter === 'all' || 
      (userTypeFilter === 'photographer' && (u.user_type === 'photographer' || !u.user_type)) ||
      (userTypeFilter === 'salon_owner' && u.user_type === 'salon_owner') ||
      (userTypeFilter === 'albumcu' && u.user_type === 'albumcu');
    
    // Durum filtresi
    const matchStatus = statusFilter === 'all' || u.subscription_status === statusFilter;
    
    return matchSearch && matchUserType && matchStatus;
  });

  // İstatistikler
  const stats = {
    total: users.length,
    photographers: users.filter(u => u.user_type === 'photographer' || !u.user_type).length,
    salons: users.filter(u => u.user_type === 'salon_owner').length,
    albumcus: users.filter(u => u.user_type === 'albumcu').length,
    pending: users.filter(u => u.subscription_status === 'pending').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Kullanıcı Yönetimi</h1>
        <p className="text-muted-foreground mt-1">Tüm fotoğrafçı ve salon hesaplarını yönetin</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Card className="p-4">
          <p className="text-2xl font-bold">{stats.total}</p>
          <p className="text-sm text-muted-foreground">Toplam Kullanıcı</p>
        </Card>
        <Card className="p-4">
          <p className="text-2xl font-bold text-blue-600">{stats.photographers}</p>
          <p className="text-sm text-muted-foreground">Fotoğrafçı</p>
        </Card>
        <Card className="p-4">
          <p className="text-2xl font-bold text-purple-600">{stats.salons}</p>
          <p className="text-sm text-muted-foreground">Düğün Salonu</p>
        </Card>
        <Card className="p-4">
          <p className="text-2xl font-bold text-emerald-600">{stats.albumcus}</p>
          <p className="text-sm text-muted-foreground">Albümcü</p>
        </Card>
        <Card className="p-4">
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
          <p className="text-sm text-muted-foreground">Onay Bekleyen</p>
        </Card>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Kullanıcı ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        {/* Kullanıcı Tipi Filtresi */}
        <div className="flex gap-2">
          <Button
            variant={userTypeFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setUserTypeFilter('all')}
          >
            Tümü
          </Button>
          <Button
            variant={userTypeFilter === 'photographer' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setUserTypeFilter('photographer')}
          >
            📷 Fotoğrafçı
          </Button>
          <Button
            variant={userTypeFilter === 'salon_owner' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setUserTypeFilter('salon_owner')}
          >
            🏛️ Salon
          </Button>
          <Button
            variant={userTypeFilter === 'albumcu' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setUserTypeFilter('albumcu')}
          >
            📚 Albümcü
          </Button>
        </div>
        
        {/* Durum Filtresi */}
        <div className="flex gap-2">
          <Button
            variant={statusFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('all')}
          >
            Tüm Durum
          </Button>
          <Button
            variant={statusFilter === 'pending' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('pending')}
            className="text-yellow-600"
          >
            Bekleyen
          </Button>
          <Button
            variant={statusFilter === 'active' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('active')}
          >
            Aktif
          </Button>
        </div>
      </div>

      {/* Users List */}
      <div className="space-y-4">
        {filteredUsers.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground">Kullanıcı bulunamadı.</p>
          </Card>
        ) : filteredUsers.map((user) => {
          const statusInfo = subscriptionStatuses[user.subscription_status] || subscriptionStatuses.active;
          const userTypeLabel = user.user_type === 'salon_owner' ? '🏛️ Salon' 
            : user.user_type === 'albumcu' ? '📚 Albümcü' 
            : '📷 Fotoğrafçı';
          
          return (
            <Card key={user.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* User Info */}
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg">{user.full_name}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          user.user_type === 'salon_owner' 
                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                            : user.user_type === 'albumcu'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        }`}>
                          {userTypeLabel}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                      {user.company_name && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <Building2 className="h-3 w-3" />
                          {user.company_name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-primary">{user.customer_count || 0}</p>
                      <p className="text-muted-foreground">Müşteri</p>
                    </div>
                    <div className="h-10 w-px bg-border" />
                    <div className="text-center">
                      <p className="font-medium">{subscriptionPlans[user.subscription_plan] || 'Belirsiz'}</p>
                      <p className="text-muted-foreground text-xs">Paket</p>
                    </div>
                    <div className="h-10 w-px bg-border" />
                    <div className="text-center">
                      <p className="font-medium flex items-center gap-1">
                        <HardDrive className="h-3 w-3 text-muted-foreground" />
                        {user.storage_used_gb?.toFixed(2) || 0} GB
                      </p>
                      <p className="text-muted-foreground text-xs">Depolama</p>
                    </div>
                    <div className="h-10 w-px bg-border" />
                    <div className="text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                        {user.subscription_status === 'active' ? (
                          <CheckCircle className="h-3 w-3" />
                        ) : user.subscription_status === 'expired' ? (
                          <AlertCircle className="h-3 w-3" />
                        ) : (
                          <Clock className="h-3 w-3" />
                        )}
                        {statusInfo.label}
                      </span>
                      {user.subscription_expires_at && (
                        <p className="text-muted-foreground text-xs mt-1">
                          {formatDate(user.subscription_expires_at.split('T')[0])}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {user.subscription_status === 'pending' && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleApproveUser(user)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`approve-user-${user.id}`}
                      >
                        <UserCheck className="h-4 w-4 mr-2" />
                        Onayla
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditSubscription(user)}
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      Abonelik
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDeleteUser(user)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Registration Date */}
                <div className="mt-4 pt-4 border-t flex items-center gap-2 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  Kayıt: {formatDate(user.created_at?.split('T')[0])}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
            <h3 className="text-lg font-medium mb-2">Kullanıcı bulunamadı</h3>
          </div>
        )}
      </div>

      {/* Subscription Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Abonelik Düzenle - {selectedUser?.full_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Paket</Label>
              <Select
                value={subscriptionForm.subscription_plan}
                onValueChange={(value) => setSubscriptionForm({ ...subscriptionForm, subscription_plan: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Paket seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Aylık Paket</SelectItem>
                  <SelectItem value="yearly">Yıllık Paket</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Durum</Label>
              <Select
                value={subscriptionForm.subscription_status}
                onValueChange={(value) => setSubscriptionForm({ ...subscriptionForm, subscription_status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktif</SelectItem>
                  <SelectItem value="expired">Süresi Dolmuş</SelectItem>
                  <SelectItem value="pending">Beklemede</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Bitiş Tarihi</Label>
              <Input
                type="date"
                value={subscriptionForm.subscription_expires_at}
                onChange={(e) => setSubscriptionForm({ ...subscriptionForm, subscription_expires_at: e.target.value })}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1">
                İptal
              </Button>
              <Button onClick={handleSaveSubscription} disabled={isSubmitting} className="flex-1">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
